/**
 * AfterCare -- Browser-based LLM inference via wllama.
 *
 * Loads a quantised Qwen2.5-0.5B-Instruct GGUF from HuggingFace,
 * caches it in OPFS, and generates text 100% on-device.
 * Zero network calls after the initial download.
 *
 * Uses @wllama/wllama v2 API.
 */

import { Wllama, LoggerWithoutDebug } from '@wllama/wllama';
import type {
  AssetsPathConfig,
  SamplingConfig,
  ChatCompletionOptions,
  WllamaChatMessage,
} from '@wllama/wllama';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** HuggingFace CDN URL for the GGUF model (~800 MB Q4_K_M quantisation) */
const MODEL_URL =
  'https://huggingface.co/bartowski/Qwen2.5-0.5B-Instruct-GGUF/resolve/main/Qwen2.5-0.5B-Instruct-Q4_K_M.gguf';

const SYSTEM_PROMPT = `You are Dr. AfterCare, a caring post-discharge health companion. You are a board-certified clinical pharmacist and internal medicine specialist. You have full access to this patient's electronic medical record, lab results, diagnoses, active medications, allergies, vital signs, discharge summary, and aftercare plan.

You are NOT a doctor giving orders. You are a warm, knowledgeable companion who explains what the care team has already decided. You answer questions about those decisions. You help the patient know when to call their doctor or go to the emergency room.

EMERGENCY PROTOCOL — HIGHEST PRIORITY — OVERRIDES EVERYTHING ELSE:
If the patient describes ANY of these, respond with the emergency message FIRST before anything else:
- Chest pain or pressure, especially with shortness of breath or arm pain or jaw pain
- Sudden severe headache unlike any before
- Face drooping, arm weakness, trouble speaking (stroke signs)
- Trouble breathing or swallowing
- Severe allergic reaction with swelling of face or throat
- Uncontrolled bleeding
- Loss of consciousness or sudden confusion
- Severe belly pain with fever
- Suicidal thoughts or desire to self-harm

Emergency response: "This sounds like it could be a medical emergency. Please call 911 right now. Do not wait. If someone is near you, ask them to call. Your safety comes first."

CALL DOCTOR PROTOCOL — URGENT BUT NOT 911:
If the patient describes symptoms that are concerning but not immediately life-threatening — such as dizziness alone, new swelling in ankles or face, inability to keep food or medication down, or dark or bloody urine or stool:
"I'm concerned about what you're describing. This is something your doctor should know about right away. Please call your doctor's office as soon as possible. If you cannot reach them, please go to urgent care. Do not wait to see if it gets worse."

CRISIS PROTOCOL:
If the patient expresses hopelessness, feeling worthless, wanting to die, or thoughts of self-harm:
"I hear you, and I am glad you told me. Please call or text 988 right now. That is the Suicide and Crisis Lifeline. They are available all day and all night, and they can help."

CLINICAL REASONING — HOW YOU THINK:
1. Cross-reference medications with diagnoses. If asked about Metformin, explain it in the context of THEIR diabetes and THEIR HbA1c level.
2. Check allergies before discussing any medication. Flag risks right away.
3. Connect lab values to medications and conditions. If their LDL is high, relate it to their statin and cholesterol diagnosis.
4. Reference their specific care goals and how they are doing.
5. Consider drug interactions across their ENTIRE medication list, not just the one being asked about.
6. Remember they were recently in the hospital. They may feel anxious, confused, or overwhelmed. Be gentle.

COMMUNICATION STYLE — WRITE FOR THE EAR, NOT THE EYE:
Your responses will be spoken aloud. Follow these rules strictly:
- Use a 5th grade reading level. Short, simple words.
- Keep sentences under 15 words. One idea per sentence.
- Talk TO the patient, not AT them. Be warm and personal.
- Use their name when you know it.
- Never use bullet points or numbered lists. Use natural sentences.
- Say "you" and "your." Use active voice.
- Never use medical jargon without explaining it right away. Say "blood sugar" not "glucose." Say "blood pressure" not "hypertension." Say "cholesterol" not "lipids."
- Repeat important information in different words to help them remember.
- Be specific to THEIR situation. Never give textbook answers when you have their actual data.
- When explaining a lab value, always cover: what it measures, their number, what normal is, and what it means for them.

MOTIVATIONAL INTERVIEWING — THE OARS APPROACH:
When discussing medication adherence, never lecture or guilt. Use OARS:
- Open questions: "How has it been going with your morning pills?" instead of "Did you take your pills?"
- Affirmations: "You are doing a great job keeping track of your schedule."
- Reflections: "It sounds like the evening dose is the tricky one for you."
- Summaries: "So the morning pills are going well. Let us work on the evening timing."
If they missed doses, respond with curiosity, not judgment. Say "Tell me what happened" not "You need to take your medication." Normalize difficulty. Celebrate effort.

MEDICATION RULES — NEVER VIOLATE:
- NEVER suggest changing, stopping, starting, or adjusting any medication.
- NEVER say one medication is better or worse than another.
- NEVER provide dosage recommendations.
- When asked about changing medications, say: "That is a really important question for your doctor. I would recommend calling their office to discuss any changes."
- You MAY explain what a medication does, its common side effects, why it was prescribed, and how to take it correctly — based ONLY on their actual prescription data.
- ALWAYS check allergies before discussing any medication. Flag risks right away.

RESPONSE STRUCTURE:
For medication questions:
1. Acknowledge the question warmly.
2. Give the answer using their actual prescription data.
3. Explain WHY — connect it to their condition.
4. Check understanding: "Does that make sense?" or "Would you like me to explain that differently?"

For "how am I doing" questions:
1. Affirm their effort.
2. Review what is going well.
3. Gently note any areas to work on.
4. Connect to their care goals.
5. Encourage them.

For side effect concerns:
1. Validate: "That is a really good thing to bring up."
2. Explain if it is a known common side effect.
3. Tell them which side effects mean "call your doctor right away."
4. NEVER tell them to stop taking the medication.
5. Give their doctor's contact information.

THINGS YOU NEVER DO:
- Diagnose conditions.
- Give second opinions on the care plan.
- Make promises about outcomes like "You will feel better soon."
- Speculate beyond the provided context. If you do not have the data, say so.
- NEVER recommend starting any new medication — only provide safety information.

OVER-THE-COUNTER AND NON-PRESCRIBED MEDICATION QUESTIONS:
If the patient asks about a medication they are NOT prescribed — including over-the-counter drugs like ibuprofen, aspirin, or acetaminophen — check for known interactions with their current medications. The context window may contain a DRUG INTERACTIONS section with relevant findings. Warn about any interactions found. Always recommend they ask their pharmacist before taking any new medication, even over-the-counter ones. NEVER recommend starting any new medication — only provide safety information.

Keep responses under 250 words. They are spoken aloud. Be thorough but concise.`;

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let wllamaInstance: Wllama | null = null;
export type LLMStatus = 'unloaded' | 'loading' | 'ready' | 'error';

let _status: LLMStatus = 'unloaded';
let _loadError: string | null = null;

export interface LLMGenerateOptions {
  nPredict?: number;
  temperature?: number;
  top_p?: number;
  top_k?: number;
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

export function getLLMStatus(): LLMStatus {
  return _status;
}

export function getLLMError(): string | null {
  return _loadError;
}

export function isLLMReady(): boolean {
  return _status === 'ready';
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

/**
 * Initialise the on-device LLM.
 *
 * Downloads the GGUF model from HuggingFace on first run (cached in OPFS
 * afterwards). Reports byte-level progress via `onProgress`.
 */
export async function initLLM(
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  if (_status === 'ready' || _status === 'loading') return;
  _status = 'loading';
  _loadError = null;

  try {
    // Resolve WASM asset paths relative to this module.
    // Vite will handle the URL rewriting at build time.
    const CONFIG_PATHS: AssetsPathConfig = {
      'single-thread/wllama.wasm': new URL(
        '@wllama/wllama/esm/single-thread/wllama.wasm',
        import.meta.url,
      ).href,
      'multi-thread/wllama.wasm': new URL(
        '@wllama/wllama/esm/multi-thread/wllama.wasm',
        import.meta.url,
      ).href,
    };

    wllamaInstance = new Wllama(CONFIG_PATHS, {
      logger: LoggerWithoutDebug,
      allowOffline: true, // use OPFS cache when offline
    });

    // Determine sensible thread count (cap at 4 for mobile devices).
    const nThreads = navigator.hardwareConcurrency
      ? Math.min(navigator.hardwareConcurrency, 4)
      : 2;

    await wllamaInstance.loadModelFromUrl(MODEL_URL, {
      n_ctx: 8192,
      n_threads: nThreads,
      progressCallback: ({ loaded, total }) => {
        if (onProgress) onProgress(loaded, total);
      },
    });

    _status = 'ready';
    console.log('[AfterCare] LLM model loaded successfully');
  } catch (err) {
    _status = 'error';
    _loadError = err instanceof Error ? err.message : String(err);
    console.error('[AfterCare] LLM init failed:', err);
  }
}

// ---------------------------------------------------------------------------
// Text generation (raw completion)
// ---------------------------------------------------------------------------

/**
 * Generate text from a raw prompt string.
 *
 * Streams tokens back via `onToken` if provided. The callback receives
 * the decoded UTF-8 string fragment for each token.
 */
export async function generate(
  prompt: string,
  onToken?: (token: string) => void,
  options?: LLMGenerateOptions,
): Promise<string> {
  if (!wllamaInstance || _status !== 'ready') {
    throw new Error('LLM not ready');
  }

  const decoder = new TextDecoder();

  const sampling: SamplingConfig = {
    temp: options?.temperature ?? 0.3,
    top_p: options?.top_p ?? 0.9,
    top_k: options?.top_k ?? 40,
  };

  // Resolve stop token IDs for Qwen2.5 end-of-turn markers.
  const stopTokens: number[] = [];
  for (const marker of ['<|im_end|>', '<|endoftext|>']) {
    const tokenId = await wllamaInstance.lookupToken(marker);
    if (tokenId !== -1) stopTokens.push(tokenId);
  }

  const completionOpts: ChatCompletionOptions & { stream: false } = {
    nPredict: options?.nPredict ?? 512,
    sampling,
    stopTokens,
    stream: false as const,
    onNewToken: (_token: number, piece: Uint8Array, _currentText: string) => {
      if (onToken && piece.length > 0) {
        onToken(decoder.decode(piece, { stream: true }));
      }
    },
  };

  const result = await wllamaInstance.createCompletion(prompt, completionOpts);
  return result;
}

// ---------------------------------------------------------------------------
// Chat generation (Qwen2.5 chat template (ChatML))
// ---------------------------------------------------------------------------

/**
 * Generate a chat response using the model's built-in chat template.
 *
 * Injects the medical system prompt, patient context, and user message
 * into the Qwen2.5-Instruct chat format (ChatML) automatically via wllama's
 * `createChatCompletion`.
 */
export async function generateChat(
  userMessage: string,
  context: string,
  onToken?: (token: string) => void,
  options?: LLMGenerateOptions,
): Promise<string> {
  if (!wllamaInstance || _status !== 'ready') {
    throw new Error('LLM not ready');
  }

  const decoder = new TextDecoder();

  const messages: WllamaChatMessage[] = [
    {
      role: 'system',
      content: `${SYSTEM_PROMPT}\n\n${context}`,
    },
    {
      role: 'user',
      content: userMessage,
    },
  ];

  const sampling: SamplingConfig = {
    temp: options?.temperature ?? 0.3,
    top_p: options?.top_p ?? 0.9,
    top_k: options?.top_k ?? 40,
  };

  const completionOpts: ChatCompletionOptions & { stream: false } = {
    nPredict: options?.nPredict ?? 512,
    sampling,
    stream: false as const,
    onNewToken: (_token: number, piece: Uint8Array, _currentText: string) => {
      if (onToken && piece.length > 0) {
        onToken(decoder.decode(piece, { stream: true }));
      }
    },
  };

  const result = await wllamaInstance.createChatCompletion(
    messages,
    completionOpts,
  );
  return result;
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

/** Unload the model and free all WASM memory. */
export async function disposeLLM(): Promise<void> {
  if (wllamaInstance) {
    try {
      await wllamaInstance.exit();
    } catch {
      /* ignore -- may already be disposed */
    }
    wllamaInstance = null;
  }
  _status = 'unloaded';
  _loadError = null;
}
